import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateUser, useUpdateUser } from '@/services/api/endpoints/UserService';
import { useGetCompanies } from '@/services/api/endpoints/CompanyService';
import { USER_ROLES } from '@/constants';
import type { User, UserRole } from '@/types';

interface UserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

interface FormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: UserRole | '';
  companyId: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  role?: string;
  companyId?: string;
}

const INITIAL_FORM: FormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  role: '',
  companyId: '',
};

const ROLES_REQUIRING_COMPANY: UserRole[] = [
  USER_ROLES.COMPANY_ADMIN as UserRole,
  USER_ROLES.COMPANY_OPERATOR as UserRole,
];

export function UserModal({ open, onOpenChange, user }: UserModalProps) {
  const { t } = useTranslation();
  const isEdit = !!user;

  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<FormErrors>({});

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const { data: companiesData } = useGetCompanies(1, 100, '', 'name', 'asc', 'active');

  const requiresCompany = ROLES_REQUIRING_COMPANY.includes(form.role as UserRole);

  useEffect(() => {
    if (open) {
      if (user) {
        setForm({
          email: user.email,
          password: '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phone: user.phone || '',
          role: user.role,
          companyId: user.companyId || '',
        });
      } else {
        setForm(INITIAL_FORM);
      }
      setErrors({});
    }
  }, [open, user]);

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));

    if (field === 'role') {
      const newRole = value as UserRole;
      if (!ROLES_REQUIRING_COMPANY.includes(newRole)) {
        setForm((prev) => ({ ...prev, role: value as UserRole | '', companyId: '' }));
        setErrors((prev) => ({ ...prev, companyId: undefined }));
      }
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!form.email.trim()) {
      newErrors.email = t('users.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = t('users.validation.emailInvalid');
    }

    if (!isEdit && !form.password.trim()) {
      newErrors.password = t('users.validation.passwordRequired');
    }

    if (!form.role) {
      newErrors.role = t('users.validation.roleRequired');
    }

    if (requiresCompany && !form.companyId) {
      newErrors.companyId = t('users.validation.companyRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload: Partial<User> & { password?: string } = {
      email: form.email,
      firstName: form.firstName || undefined,
      lastName: form.lastName || undefined,
      phone: form.phone || undefined,
      role: form.role as UserRole,
      companyId: requiresCompany ? form.companyId : undefined,
    };

    try {
      if (isEdit) {
        const { email, ...updatePayload } = payload;
        await updateUser.mutateAsync({ id: user.id, ...updatePayload });
        toast.success(t('users.userUpdated'));
      } else {
        payload.password = form.password;
        await createUser.mutateAsync(payload);
        toast.success(t('users.userCreated'));
      }
      onOpenChange(false);
    } catch {
      toast.error(isEdit ? t('users.userUpdateError') : t('users.userCreateError'));
    }
  };

  const isPending = createUser.isPending || updateUser.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('users.editUser') : t('users.createUser')}</DialogTitle>
          <DialogDescription>
            {isEdit ? t('users.editUserDesc') : t('users.createUserDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Email */}
          <div className="grid gap-2">
            <Label htmlFor="email">{t('users.email')} *</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              disabled={isEdit}
              error={errors.email}
              placeholder="user@example.com"
            />
          </div>

          {/* Password (create only) */}
          {!isEdit && (
            <div className="grid gap-2">
              <Label htmlFor="password">{t('auth.password')} *</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value)}
                error={errors.password}
              />
            </div>
          )}

          {/* First Name */}
          <div className="grid gap-2">
            <Label htmlFor="firstName">{t('users.firstName')}</Label>
            <Input
              id="firstName"
              value={form.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
            />
          </div>

          {/* Last Name */}
          <div className="grid gap-2">
            <Label htmlFor="lastName">{t('users.lastName')}</Label>
            <Input
              id="lastName"
              value={form.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
            />
          </div>

          {/* Phone */}
          <div className="grid gap-2">
            <Label htmlFor="phone">{t('users.phone')}</Label>
            <Input
              id="phone"
              value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </div>

          {/* Role */}
          <div className="grid gap-2">
            <Label>{t('users.role')} *</Label>
            <Select
              value={form.role}
              onValueChange={(value) => handleChange('role', value)}
            >
              <SelectTrigger className={errors.role ? 'border-destructive' : ''}>
                <SelectValue placeholder={t('users.selectRole')} />
              </SelectTrigger>
              <SelectContent>
                {Object.values(USER_ROLES).map((role) => (
                  <SelectItem key={role} value={role}>
                    {t(`users.roles.${role}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.role && <p className="text-sm text-destructive">{errors.role}</p>}
          </div>

          {/* Company (shown when role requires it) */}
          {requiresCompany && (
            <div className="grid gap-2">
              <Label>{t('users.company')} *</Label>
              <Select
                value={form.companyId}
                onValueChange={(value) => handleChange('companyId', value)}
              >
                <SelectTrigger className={errors.companyId ? 'border-destructive' : ''}>
                  <SelectValue placeholder={t('users.selectCompany')} />
                </SelectTrigger>
                <SelectContent>
                  {companiesData?.items.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.companyId && (
                <p className="text-sm text-destructive">{errors.companyId}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} isLoading={isPending}>
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
